<template>
  <sections-extra-layout>
    <template slot="title">Page targeting</template>
    <template slot="description">Define where the {{ type }} should appear.</template>
    <template slot="content">
      <div>
        <label class="cursor-pointer w-full mb-2 inline-flex items-center">
          <span class="font-bold">What is the domain associated for this {{ type }}?</span>
          <input
            v-on:input='handleUrlError()'
            class="
              mx-4 px-3 py-3
              placeholder-gray-400
              text-gray-700
              relative
              bg-white
              bg-white
              rounded
              text-sm
              shadow
              outline-none
              focus:outline-none
              focus:shadow-outline
              w-1/2"
            v-model="url"
          >
        </label>

        <p class="font-bold mb-4">Which page should we show this content on?</p>
        <div class="my-2">
          <label
            class="cursor-pointer w-full mb-4 inline-flex items-center ml-6">
            <input
              @click="pathsTarget = '.*'"
              @change="emitPathsTarget()"
              type="radio"
              class="form-radio"
              name="url_target"
              v-model="selectedRegex"
              value="all">
            <span class="ml-2">Any page</span>
          </label>

          <label
            class="cursor-pointer w-full mb-2 inline-flex ml-6">
            <input
              @click="resetPathsTarget()"
              type="radio"
              class="form-radio h-4 my-3"
              name="url_target"
              v-model="selectedRegex"
              value="custom">
            <span class="ml-2 py-3">At a list of specific path (separate with ',')</span>

            <div class="w-1/2 flex flex-col">
              <input
                v-show="selectedRegex === 'custom'"
                type="text"
                v-model="pathsTarget"
                @change="emitPathsTarget()"
                v-on:input="handlePathTargetError()"
                placeholder="/rocketfid/campaign, /recommendation, /campaigns/*/sp?activeStep=2&period=*"
                class="
                  mx-4 px-3 py-3
                  placeholder-gray-400
                  text-gray-700
                  relative
                  bg-white
                  rounded
                  text-sm
                  shadow
                  outline-none
                  focus:outline-none
                  focus:shadow-outline
                  w-1/1" />
              <span
                v-show="selectedRegex === 'custom'"
                class="
                  cursor-default
                  mx-7
                  py-2"
              >
                <div class="flex flex-row">
                  <span>Use "*" to allow any content at its place. 
                  <br>Use "?activeStep=*&amp;period=7" to manage query parameters</span>
                  <span></span>
                  <div
                    @click="helpCustomRegex = !helpCustomRegex"
                    class="flex items-center justify-center ml-3 h-5 w-5 cursor-pointer bg-black text-white rounded-full text-center font-bold"
                  >
                    <span>?</span>
                  </div>
                </div>
                <div
                  v-show="selectedRegex === 'custom' && helpCustomRegex === true"
                >
                  <u>Example : <span class="font-bold italic">/user/*/message/</span></u>
                  <div class="flex flex-row">
                    <div class="flex flex-col font-bold mr-3">
                      <span style="color: #10B981">✓</span>
                      <span style="color: #10B981">✓</span>
                      <span style="color: #D97706">✗</span>
                      <span style="color: #D97706">✗</span>
                    </div>
                    <div class="flex flex-col">
                      <span>/user/<b>1234</b>/message/</span>
                      <span>/user/<b>patrick</b>/message/</span>
                      <span>/user/message/</span>
                      <span>/user/<b>567/patrick</b>/message/</span>
                    </div>
                  </div><br>

                  <!-- -->
                  <u> Example: <span class="font-bold italic">/campaigns/*/sp?activeStep=2</span></u>
                  <div class="flex flex-row">
                    <div class="flex flex-col font-bold mr-3">
                      <span style="color: #10B981">✓</span>
                      <span style="color: #10B981">✓</span>
                      <span style="color: #D97706">✗</span>
                      <span style="color: #D97706">✗</span>
                    </div>
                    <div class="flex flex-col">
                      <span>/campaigns/12/sp?<b>activeStep=2</b></span>
                      <span>/campaigns/24/sp/?p=ch&amp;<b>activeStep=2</b></span>
                      <span>/campaigns/32/sp?<b>activeStep=4</b></span>
                      <span>/campaigns/42/sp?param1=chaval&amp;param2=2</span>
                    </div>
                  </div><br>

                  <u> Example: <span class="font-bold italic">/campaigns/*/sp?activeStep=*&amp;period=7</span></u>
                  <div class="flex flex-row">
                    <div class="flex flex-col font-bold mr-3">
                      <span style="color: #10B981">✓</span>
                      <span style="color: #10B981">✓</span>
                      <span style="color: #D97706">✗</span>
                      <span style="color: #D97706">✗</span>
                    </div>
                    <div class="flex flex-col">
                      <span>/campaigns/12/sp?<b>period=7&activeStep=2</b></span>
                      <span>/campaigns/24/sp?<b>activeStep=3&amp;period=7</b>&amp;param=126</span>
                      <span>/campaigns/32/sp?activeStep=4&amp;<b>period=12</b></span>
                      <span>/campaigns/42/sp?param1=chaval&amp;param2=2</span>
                    </div>
                  </div>
                  <!-- -->
                </div>
                



              </span>
            </div>
            <span
              v-if="error && error.path && !error.path.status"
              class="text-red-500 font-bold m-3">
              {{ error.path.message ? error.path.message : '' }}
            </span>
          </label>

        </div>
        <p class="font-bold mb-4">The {{ type }} will be target on the following url:</p>
        <div class="mt-2">
          <label class="cursor-pointer w-full mb-2 inline-flex items-center">
            <input
              title="Modify URL and DOMAIN instead on top instead."
              disabled
              type="text"
              :placeholder="placeholderPath"
              class="
                disabled:opacity-50
                cursor-not-allowed
                mx-4 px-3 py-3
                placeholder-gray-600
                text-gray-900
                relative
                bg-white
                rounded
                text-sm
                shadow
                outline-none
                focus:outline-none
                focus:shadow-outline
                w-1/2"/>
            <span
              v-if="error && error.url && !error.url.status"
              class="text-red-500 font-bold m-4">
              {{ error.url.message ? error.url.message : ''}}
            </span>
          </label>
        </div>
        <p class="font-bold mb-4 mt-4">Which page should we exclude this content?</p>
        <div class="mt-2">
          <label
            class="w-full mb-2 inline-flex items-center ml-6">
            
            <span class="">Provide a list of specific path (separate with ',')</span>

            <input
              type="text"
              v-model="pathsExclude"
              @change="emitPathsExclude()"
              v-on:input="handlePathExcludeError()"
              placeholder="/rocketfid/campaign, /recommendation"
              class="
                mx-4 px-3 py-3
                placeholder-gray-400
                text-gray-700
                relative
                bg-white
                rounded
                text-sm
                shadow
                outline-none
                focus:outline-none
                focus:shadow-outline
                w-1/2" />
            <span
              v-if="error && error.pathExclude && !error.pathExclude.status"
              class="text-red-500 font-bold m-4">
              {{ error.pathExclude.message ? error.pathExclude.message : '' }}
            </span>
          </label>
        </div>
      </div>
    </template>
  </sections-extra-layout>
</template>

<script>
export default {
  name: 'Targeting',
  props: {
    type: {
      type: String,
      required: true
    },
    currentUrl: {
      type: String,
      required: false
    },
    targetUrl: {
      type: String,
      required: false
    },
    targetUrlList: {
      type: Array,
      required: false
    },
    excludeUrl: {
      type: Array,
      required: false
    }
  },
  data () {
    return {
      url: null,
      pathsTarget: '',
      selectedRegex: 'all',
      helpCustomRegex: false,
      error: {
        path: null,
        url: null
      },
      pathsExclude: ''
    }
  },
  computed: {
    placeholderPath () {
      if (this.selectedRegex === 'all') {
        return this.url ? this.url : ''
      } 
      
      let url = this.url ? this.url : ''
      let paths = this.pathsTarget ? this.pathsToArray(this.pathsTarget) : []
      let placeholder = ''
      paths.forEach((path, index) => {
        if (index === 0) {
          placeholder += url + path
        } else {
          placeholder += ', ' + url + path
        }
      });
      
      return placeholder.length == 0 ? url : placeholder
    }
  },
  watch: {
    'url': function (value) {
      if (value && this.handleUrlError) {
        this.$emit('update-fields-url', this.url)
      }
    },
  },
  methods: {
    resetPathsTarget () {
      if (this.pathsTarget == '.*') {
        this.pathsTarget = '';
      }
    },
    emitPathsTarget () {
      if (this.handlePathTargetError()) {
        let paths = this.pathsToArray(this.pathsTarget)
        if (paths == []) {
          paths = ['.*']
        }
        this.$emit('update-path-target', paths)
      }
    },
    emitPathsExclude () {
      if (this.handlePathExcludeError()) {
        let paths = this.pathsToArray(this.pathsExclude)
        this.$emit('update-path-exclude', paths)
      }
    },
    pathsToArray (input) {
      let paths = input.split(',')
      paths = paths.map(path => path.trim())
      return paths;
    },
    handlePathTargetError () {
      if (this.selectedRegex === 'all') {
        return true;
      }
      let valide = true
      if (this.pathsTarget) {
        for (let path of this.pathsToArray(this.pathsTarget)) {
          if (!path.startsWith('/')) {
            this.error.path = {
              message: this.$root.$t('banner.error.pathTarget.start'),
              status: false
            }
            valide = false;
          } else if (path.includes('//')) {
            this.error.path = {
              message: this.$root.$t('banner.error.pathTarget.double'),
              status: false
            }
            valide = false;
          }
        }
      }
      if (!valide) { return false }
      if (this.error.path && this.error.path.message) {
        this.error.path.message = null
      }
      return true;
    },
    handlePathExcludeError () {
      let valide = true
      if (this.pathsExclude) {
        for (let path of this.pathsToArray(this.pathsExclude)) {
          if (!path.startsWith('/')) {
            this.error.pathExclude = {
              message: this.$root.$t('banner.error.pathTarget.start'),
              status: false
            }
            valide = false;
          } else if (path.includes('//')) {
            this.error.pathExclude = {
              message: this.$root.$t('banner.error.pathTarget.double'),
              status: false
            }
            valide = false;
          }
        }
      }
      if (!valide) { return false }
      if (this.error.pathExclude && this.error.pathExclude.message) {
        this.error.pathExclude.message = null
      }
      return true;
    },

    handleUrlError () {
      if (this.url) {
        if (!this.validateUrl(this.url)) {
          this.error.url = {
            message: this.$root.$t('banner.error.url.invalid'),
            status: false
          }

          return false
        }
      }

      if (this.error.url && this.error.url.message) {
        this.error.url.message = null
      }

      return true
    },

    validateUrl (str) {
      const regexp = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/
      let valide = true
      let paths = str.split(',')
      paths = paths.map(path => path.trim());
      for (let path of paths) {
        valide = valide && (!this.url.includes('/') && regexp.test(path))
      }
      return valide;
    }
  },

  mounted () {
    if (this.currentUrl) { 
      this.url = this.currentUrl 
    }

    if (this.targetUrl && this.targetUrlList[0] === undefined) {
      // migrate targetUrl to targetUrlList
      this.pathsTarget = this.targetUrl;
      if (this.targetUrl !== '.*') {
        this.selectedRegex = 'custom'
      } else {
        this.selectedRegex = 'all'
      }
    } else if (this.targetUrlList[0] && this.targetUrlList[0] === '.*') {
      // any page
      this.pathsTarget = this.targetUrlList[0]
      this.selectedRegex = 'all'
    } else if (this.targetUrlList[0]) {
      // page custom
      this.selectedRegex = 'custom' 
      this.pathsTarget = '';
      let newPath = '';
      this.targetUrlList.forEach((path, index) => {
        if (index === 0) {
          newPath += path;
        } else {
          newPath += ', ' + path;
        }
      });
      this.pathsTarget = newPath;
    } else {
      // default case
      this.pathsTarget = '.*';
      this.selectedRegex = 'all'
    }

    this.emitPathsTarget()

    if (this.excludeUrl) {
      this.pathsExclude = '';
      let newPath = '';
      this.excludeUrl.forEach((path, index) => {
        if (index === 0) {
          newPath += path;
        } else {
          newPath += ', ' + path;
        }
      });
      this.pathsExclude = newPath;
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/common/ui/form/targeting.vue